import React from "react";
import {
    Box,
    Typography,
    Button,
    IconButton,
} from "@mui/material";
import ImageIcon from "@mui/icons-material/Image";
import MessageIcon from "@mui/icons-material/Message";

export default function InteractiveMessage({ msg }) {

    if (msg.media_type !== "interactive") return null;

    const renderHeader = () => {
        const header = msg?.interactive_content?.header;
        if (!header) return null;

        if (header.type === "image") {
            return (
                <Box
                    component="img"
                    src={header.image?.link}
                    alt="Interactive Header"
                    style={{
                        width: "100%",
                        height: "200px",
                        objectFit: "contain",
                        borderRadius: 8,
                        border: "1px solid var(--disabled-color)",
                        zIndex: 100
                    }}

                />
            );
        }

        if (header.type === "video") {
            return (
                <Box
                    component="video"
                    src={header.video?.link}
                    controls
                    style={{ maxWidth: "100%", borderRadius: 8, border: "1px solid var(--disabled-color)", zIndex: 100 }}
                />
            );
        }

        return null;
    };

    const renderBody = () => {
        const body = msg?.interactive_content?.body?.text;
        if (!body) return null;

        return (
            <Box sx={{ mb: 1 }}>
                <Typography variant="body2" color="textSecondary">
                    {body
                        .replace(/\*([^*]+)\*/g, "⟨strong⟩$1⟨/strong⟩")
                        .split(/\\n/)
                        .map((line, idx) => (
                            <React.Fragment key={idx}>
                                {line
                                    .split(/⟨strong⟩|⟨\/strong⟩/)
                                    .map((part, i) =>
                                        i % 2 === 1 ? <strong key={i}>{part}</strong> : part
                                    )}
                                <br />
                            </React.Fragment>
                        ))}
                </Typography>
            </Box>
        );
    };

    const renderButtons = () => {
        const buttons = msg?.interactive_content?.action?.buttons;
        if (!buttons) return null;

        return (
            <Box sx={{ mt: 1 }}>
                {buttons.map((btn) => (
                    <Button
                        key={btn?.reply?.id}
                        variant="contained"
                        fullWidth
                        sx={{
                            mb: 0.5,
                            py: 0.9,
                            textTransform: "none",
                            fontSize: 14,
                            fontWeight: 600,
                            color: "#fff",
                            background: "linear-gradient(135deg, #2563eb, #3b82f6)",
                            boxShadow: "0 2px 6px -2px rgba(37,99,235,0.5)",
                            borderRadius: 2,
                            "&:hover": { background: "linear-gradient(135deg, #1d4ed8, #2563eb)", boxShadow: "0 4px 10px -3px rgba(37,99,235,0.6)" },
                        }}
                    >
                        {btn?.reply?.title}
                    </Button>
                ))}
            </Box>
        );
    };

    const renderList = () => {
        const { body, action } = msg?.interactive_content || {};
        return (
            <Box sx={{ mb: 1 }}>
                {body?.text && (
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                        {body.text}
                    </Typography>
                )}
                {action && (
                    <Button variant="contained" fullWidth sx={{
                            mb: 0.5,
                            py: 0.9,
                            textTransform: "none",
                            fontSize: 14,
                            fontWeight: 600,
                            color: "#fff",
                            background: "linear-gradient(135deg, #2563eb, #3b82f6)",
                            boxShadow: "0 2px 6px -2px rgba(37,99,235,0.5)",
                            borderRadius: 2,
                            "&:hover": { background: "linear-gradient(135deg, #1d4ed8, #2563eb)", boxShadow: "0 4px 10px -3px rgba(37,99,235,0.6)" },
                        }}>
                        <IconButton size="small" sx={{ color: "#fff" }}>
                            <ImageIcon />
                        </IconButton>
                        {action.button}
                    </Button>
                )}
            </Box>
        );
    };

    const renderLocationRequest = () => {
        const action = msg?.interactive_content?.action;
        if (!action) return null;

        return (
            <Box sx={{ mb: 1 }}>
                <Button variant="contained" fullWidth sx={{
                            mb: 0.5,
                            py: 0.9,
                            textTransform: "none",
                            fontSize: 14,
                            fontWeight: 600,
                            color: "#fff",
                            background: "linear-gradient(135deg, #2563eb, #3b82f6)",
                            boxShadow: "0 2px 6px -2px rgba(37,99,235,0.5)",
                            borderRadius: 2,
                            "&:hover": { background: "linear-gradient(135deg, #1d4ed8, #2563eb)", boxShadow: "0 4px 10px -3px rgba(37,99,235,0.6)" },
                        }}>
                    <MessageIcon sx={{ mr: 1 }} />
                    {action.name}
                </Button>
            </Box>
        );
    };


    const renderFinal = () => {
        const { body } = msg?.interactive_content || {};
        // Convert https links to clickable links
        const linkify = (text) => {
            let formatted = text.replace(/\*([^*]+)\*/g, "⟨strong⟩$1⟨/strong⟩");
            formatted = formatted.replace(/(https?:\/\/[^\s]+)/g, function(url) {
                return `<a href="${url}" target="_blank" rel="noopener noreferrer" style="color:#1976d2;text-decoration:underline;">${url}</a>`;
            });
            return formatted;
        };
        return (
            <Box sx={{ mb: 1 }}>
                <Typography
                    variant="body2"
                    sx={{
                        color: "text.secondary",
                        whiteSpace: "pre-wrap", // keeps line breaks and spacing
                        wordBreak: "break-word", // breaks long words properly
                    }}
                >
                    <span dangerouslySetInnerHTML={{ __html: linkify(body?.text || "") }} />
                </Typography>
            </Box>
        )
    };


    return (
        <Box sx={{ textAlign: "center" }}>
            {msg?.interactive_content?.type === "button" && (
                <Box sx={{ width: "100%", mx: "auto", mb: 2 }}>
                    {renderHeader()}
                    {renderBody()}
                    {renderButtons()}
                </Box>
            )}

            {msg?.interactive_content?.type === "list" && (
                <Box sx={{ width: "100%", mx: "auto", mb: 2 }}>{renderList()}</Box>
            )}

            {msg?.interactive_content?.type === "location_request_message" && (
                <Box sx={{ width: "100%", mx: "auto", mb: 2 }}>
                    {renderLocationRequest()}
                </Box>
            )}

            {msg?.interactive_content?.type === "final" && (
                <Box sx={{ width: "100%", mx: "auto", mb: 2 }}>
                    {renderFinal()}
                </Box>
            )}

        </Box>
    );
}
